import { ThumbsUpIcon } from "lucide-react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";

import {
  formatExploreImageCredit,
  toObjectPosition,
} from "@/lib/exploreImages";
import { ExploreImageAttribution, ExploreImageFocus } from "@/types";

// Mock-ups of the worker app surfaces that show explore images, at their real
// point sizes, so admins can check each crop before it ships. Every surface
// cover-crops the same image, anchored at `focus`.

// iPhone 15/16 logical width; the app's cards are this minus 16pt each side.
const PHONE_WIDTH = 390;
const FEED_CARD_WIDTH = PHONE_WIDTH - 32;
// ImageJobCard's photo height and ExploreFeedHeader's carousel card width.
const CARD_IMAGE_HEIGHT = 124;
const CAROUSEL_CARD_WIDTH = 203;
// JobPreviewSheetContent's banner and JobHeroBanner's height.
const PREVIEW_SHEET_IMAGE_HEIGHT = 160;
const SIGNUP_HERO_HEIGHT = 176;

const CroppedImage = ({
  className,
  focus,
  height,
  url,
  width,
}: {
  className?: string;
  focus: ExploreImageFocus;
  height: number;
  url: string;
  width: number | string;
}) => (
  <img
    alt=""
    className={className}
    src={url}
    style={{
      height,
      width,
      objectFit: "cover",
      objectPosition: toObjectPosition(focus),
    }}
  />
);

const PhoneFrame = ({ children }: { children: React.ReactNode }) => (
  <Column
    className="relative mx-auto overflow-hidden rounded-[36px] border-[8px] border-[#2B2D31] bg-white shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)] ring-1 ring-white/10"
    style={{ width: PHONE_WIDTH + 16 }}
  >
    <div className="mx-auto mb-1 mt-2 h-[22px] w-[96px] shrink-0 rounded-full bg-[#2B2D31]" />
    {children}
  </Column>
);

// Mirrors ImageJobCard: photo with the bottom gradient, logo tile, thumbs-up,
// then the title block underneath.
const MockJobCard = ({
  focus,
  url,
  width,
}: {
  focus: ExploreImageFocus;
  url: string;
  width: number;
}) => (
  <Column className="shrink-0" style={{ width }}>
    <div className="relative">
      <CroppedImage
        focus={focus}
        height={CARD_IMAGE_HEIGHT}
        url={url}
        width={width}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(rgba(31,34,35,0) 17%, rgba(31,34,35,0.39) 41%, rgba(31,34,35,0.7) 100%)",
        }}
      />
      <div className="absolute bottom-0 left-0 flex h-10 w-10 items-center justify-center bg-white text-xs text-slate-900">
        AM
      </div>
      <ThumbsUpIcon className="absolute right-2 top-2 h-5 w-5 text-white" />
    </div>
    <Column className="gap-1 pt-2">
      <span className="truncate text-sm font-medium text-slate-900">
        CNC Machinist
      </span>
      <span className="truncate text-xs text-slate-500">
        Acme Manufacturing • Columbus, OH
      </span>
    </Column>
  </Column>
);

export const MobileFeedPreview = ({
  focus,
  url,
}: {
  focus: ExploreImageFocus;
  url: string;
}) => (
  <PhoneFrame>
    <span className="px-4 pt-4 text-sm font-semibold">
      High paying jobs near you
    </span>
    <Row className="gap-4 overflow-x-auto px-4 py-3">
      {[0, 1].map((i) => (
        <MockJobCard
          key={i}
          focus={focus}
          url={url}
          width={CAROUSEL_CARD_WIDTH}
        />
      ))}
    </Row>
    <Column className="gap-7 px-4 pb-6 pt-2">
      {[0, 1].map((i) => (
        <MockJobCard key={i} focus={focus} url={url} width={FEED_CARD_WIDTH} />
      ))}
    </Column>
  </PhoneFrame>
);

// The sheet is where the photo shows large, so it carries the licence credit:
// one faint line in the corner, as the app will draw it.
export const JobPreviewSheetPreview = ({
  attribution,
  focus,
  url,
}: {
  attribution: ExploreImageAttribution | null;
  focus: ExploreImageFocus;
  url: string;
}) => (
  <PhoneFrame>
    <Column className="gap-3 p-4">
      <div className="mx-auto h-1 w-10 rounded-full bg-slate-300" />
      <div className="relative">
        <CroppedImage
          className="block rounded-xl"
          focus={focus}
          height={PREVIEW_SHEET_IMAGE_HEIGHT}
          url={url}
          width="100%"
        />
        {attribution && (
          <span className="absolute bottom-1.5 right-2.5 max-w-[85%] truncate text-[9px] leading-none text-white/60 [text-shadow:0_1px_2px_rgba(0,0,0,0.6)]">
            {formatExploreImageCredit(attribution)}
          </span>
        )}
      </div>
      <span className="text-lg font-semibold">CNC Machinist</span>
      <span className="text-sm text-slate-500">
        Acme Manufacturing • Columbus, OH
      </span>
    </Column>
  </PhoneFrame>
);

export const SignupHeroPreview = ({
  focus,
  url,
}: {
  focus: ExploreImageFocus;
  url: string;
}) => (
  <PhoneFrame>
    <CroppedImage
      focus={focus}
      height={SIGNUP_HERO_HEIGHT}
      url={url}
      width="100%"
    />
    <Column className="gap-2 p-4">
      <span className="text-lg font-semibold">CNC Machinist</span>
      <span className="text-sm text-slate-500">
        Sign up to apply in under a minute.
      </span>
    </Column>
  </PhoneFrame>
);
